import React, { useState } from 'react';
import { Button, Input, Spin, message } from 'antd';
import { DeleteOutlined, SendOutlined } from '@ant-design/icons';
import {
    useGetSourceCommentsQuery,
    useAddSourceCommentMutation,
    useDeleteSourceCommentMutation,
    useAddSourceReplyMutation,
    useDeleteSourceReplyMutation,
} from '../../redux/api/compassPortal';

const formatTime = (ts) => {
    if (!ts) return '';
    try {
        const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

const SourceCommentThread = ({ projectId, sourceId, isAdmin }) => {
    const { data: comments, isLoading } = useGetSourceCommentsQuery({ projectId, sourceId });
    const [addComment] = useAddSourceCommentMutation();
    const [deleteComment] = useDeleteSourceCommentMutation();
    const [addReply] = useAddSourceReplyMutation();
    const [deleteReply] = useDeleteSourceReplyMutation();

    const [newComment, setNewComment] = useState('');
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            await addComment({ projectId, sourceId, text: newComment.trim() }).unwrap();
            setNewComment('');
        } catch {
            message.error('Failed to add comment');
        }
    };

    const handleAddReply = async (commentId) => {
        if (!replyText.trim()) return;
        try {
            await addReply({ projectId, sourceId, commentId, text: replyText.trim() }).unwrap();
            setReplyText('');
            setReplyingTo(null);
        } catch {
            message.error('Failed to add reply');
        }
    };

    if (isLoading) return <Spin size="small" />;

    return (
        <div className="cp-comment-thread">
            <div className="cp-comment-thread__title">Discussion</div>

            {(!comments || comments.length === 0) && (
                <div style={{ color: 'var(--medium-light-grey-color)', fontSize: 13, padding: '8px 0' }}>
                    No comments yet. Start the discussion below.
                </div>
            )}

            {(comments || []).map((comment) => (
                <div key={comment.comment_id} className="cp-comment-item">
                    <div className="cp-comment-item__header">
                        <span className="cp-comment-item__author">{comment.author}</span>
                        <span className="cp-comment-item__time">{formatTime(comment.timestamp)}</span>
                        {isAdmin && (
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => deleteComment({ projectId, sourceId, commentId: comment.comment_id })}
                            />
                        )}
                    </div>
                    <div className="cp-comment-item__text">{comment.text}</div>

                    {/* Replies */}
                    {(comment.replies || []).map((reply) => (
                        <div key={reply.id} className="cp-comment-reply">
                            <div className="cp-comment-item__header">
                                <span className="cp-comment-item__author">{reply.author}</span>
                                <span className="cp-comment-item__time">{formatTime(reply.timestamp)}</span>
                                {isAdmin && (
                                    <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => deleteReply({ projectId, sourceId, commentId: comment.comment_id, replyId: reply.id })}
                                    />
                                )}
                            </div>
                            <div className="cp-comment-item__text">{reply.text}</div>
                        </div>
                    ))}

                    {/* Reply button / form */}
                    {replyingTo === comment.comment_id ? (
                        <div className="cp-comment-reply-form">
                            <Input.TextArea
                                rows={2}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAddReply(comment.comment_id); } }}
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                <Button size="small" type="primary" onClick={() => handleAddReply(comment.comment_id)}>Reply</Button>
                                <Button size="small" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</Button>
                            </div>
                        </div>
                    ) : (
                        <Button type="link" size="small" onClick={() => setReplyingTo(comment.comment_id)} style={{ padding: 0, fontSize: 12 }}>
                            Reply
                        </Button>
                    )}
                </div>
            ))}

            {/* New comment input */}
            <div className="cp-comment-input">
                <Input.TextArea
                    rows={2}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                />
                <Button
                    type="primary"
                    size="small"
                    icon={<SendOutlined />}
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    style={{ marginTop: 4, alignSelf: 'flex-end' }}
                >
                    Comment
                </Button>
            </div>
        </div>
    );
};

export default SourceCommentThread;
